import { type DocNode } from '@atlaskit/adf-schema';
import { traverse } from '@atlaskit/adf-utils/traverse';
import { type Schema } from '@atlaskit/editor-prosemirror/model';
import { fg } from '@atlaskit/platform-feature-flags';

import type { StreamingCommand } from '../../aiStreamingOrchestratorPluginType';

/**
 * Updates insert after docs by adding a local id of depth=1 if it doesn't exist (and returning the local id)
 * This is for the command queue to "correct" insertAfter commands if they are using the same local id to use the
 * end of the previous command.
 */
export function updateInsertAfterDoc(
	adfResponse: DocNode | undefined,
	type: StreamingCommand['type'] | undefined,
	schema: Schema | undefined,
): { localId: string | undefined; updatedResponse: DocNode | undefined } {
	if (!adfResponse || type !== 'insertNodeAfter' || !fg('platform_editor_ai_aifc_patch_ga')) {
		return { updatedResponse: adfResponse, localId: undefined };
	}
	const indexMap: number[] = [];
	const newLocalId = crypto.randomUUID();

	// First pass just to get the node to update
	traverse(adfResponse, {
		any: (_n, _p, index, depth) => {
			if (depth === 1) {
				indexMap.push(index);
			}
		},
	});

	// Update the localId of the last node
	let localIdUpdated = false;
	const newAdfResponse = traverse(
		{ ...adfResponse },
		{
			any: (node, _p, index, depth) => {
				if (
					depth === 1 &&
					indexMap[indexMap.length - 1] === index &&
					!!schema?.nodes[node.type]?.spec?.attrs?.localId
				) {
					localIdUpdated = true;
					return { ...node, attrs: { ...node.attrs, localId: newLocalId } };
				}
			},
		},
	) as DocNode;

	return {
		updatedResponse: newAdfResponse ? newAdfResponse : adfResponse,
		localId: localIdUpdated ? newLocalId : undefined,
	};
}
